import { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, Minimize2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLocation, useNavigate } from 'react-router-dom';
import { MarkdownMessage } from './MarkdownMessage';
import { useAuth } from '@/hooks/useAuth';

import chatbotLogo from "@/assets/lexique-chatbot-logo-blue.png";
import { logger } from '@/lib/logger';

interface NavigationAction {
  label: string;
  path: string;
}

interface SearchResult {
  type: 'providers' | 'missions';
  summary: string;
  count: number;
  categories: string[];
}

interface Message {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  isVoice?: boolean;
  similarity?: number;
  navigationActions?: NavigationAction[];
  searchResults?: SearchResult | null;
}

interface FAQChatbotProps {
  initialQuestion?: string;
  onOpen?: () => void;
}

export const FAQChatbot = ({ initialQuestion, onOpen }: FAQChatbotProps = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { roles } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'bot',
      content: '👋 Salut ! Moi c\'est QuiQuoiOùQuand (ou QQOQ pour les intimes), votre assistant qui sait (presque) tout ! 🤓\n\nVous avez une question ? Je suis là pour ça (et j\'adore les questions) !',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const currentRole = roles.length > 0 
    ? roles.find(r => r.role === 'prestataire')?.role || roles.find(r => r.role === 'client')?.role || 'anonymous'
    : 'anonymous';

  useEffect(() => {
    if (initialQuestion && !isOpen) {
      setIsOpen(true);
      setInput(initialQuestion);
      onOpen?.();
    }
  }, [initialQuestion, isOpen, onOpen]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Hide chatbot on lexique page - AFTER all hooks
  if (location.pathname === '/lexique') {
    return null;
  }


  const handleNavigate = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const conversationHistory = [...messages, userMessage].map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));

      const { data, error } = await supabase.functions.invoke('faq-chatbot', {
        body: { 
          messages: conversationHistory,
          userRole: currentRole
        }
      });

      if (error) throw error;

      const bestSimilarity = data.relatedFaqs?.[0]?.similarity;

      let formattedAnswer = data.answer;
      if (bestSimilarity !== undefined) {
        const percentSimilarity = (bestSimilarity * 100).toFixed(0);
        formattedAnswer = `📊 Pertinence: ${percentSimilarity}%\n\n${data.answer}`;
      }

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: formattedAnswer,
        timestamp: new Date(),
        similarity: bestSimilarity,
        navigationActions: data.navigationActions || [],
        searchResults: data.searchResults || null
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      logger.error('Erreur chatbot:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'obtenir une réponse. Veuillez réessayer.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSendClick = () => {
    sendMessage();
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 rounded-full w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 shadow-2xl z-50 p-0 overflow-hidden hover:scale-110 transition-transform"
        size="icon"
        style={{ background: 'transparent', border: 'none' }}
      >
        <img src={chatbotLogo} alt="Assistant QQOQ" className="w-full h-full object-cover" loading="lazy" />
      </Button>
    );
  }

  return (
    <Card 
      ref={cardRef}
      className={`fixed bottom-6 right-6 shadow-2xl z-50 flex flex-col transition-all duration-300 ${
        isMobile 
          ? (isMinimized ? 'w-[calc(100vw-2rem)] h-14 bottom-4 right-4 left-4' : 'w-[calc(100vw-2rem)] h-[calc(100vh-8rem)] bottom-4 right-4 left-4')
          : (isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]')
      }`}
    >
      {/* Header */}
      <div 
        className={`flex items-center justify-between border-b bg-gradient-to-r from-blue-500 to-blue-600 rounded-t-lg ${
          isMobile ? 'p-3' : 'p-4'
        }`}
      >
        <div className="flex items-center gap-2 md:gap-3">
          <img src={chatbotLogo} alt="Logo" className="w-8 h-8 md:w-12 md:h-12" loading="lazy" />
          <h3 className={`font-semibold text-white ${isMobile ? 'text-xs md:text-sm' : 'text-xs md:text-base'}`}>
            {isMobile ? 'Assistant QQOQ' : 'Assistant QuiQuoiOùQuand'}
          </h3>
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white hover:bg-blue-700"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            <Minimize2 className="h-4 w-4" />
          </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-blue-700"
              onClick={() => {
                setIsOpen(false);
              }}
            >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className="flex flex-col items-start gap-1">
                    <div
                      className={`rounded-lg ${
                        isMobile ? 'max-w-[85%] p-2.5 text-sm' : 'max-w-[80%] p-3'
                      } ${
                        message.type === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      <MarkdownMessage content={message.content} />
                      <p className="text-xs opacity-70 mt-1">
                        {message.timestamp.toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    
                    {/* Search results summary */}
                    {message.type === 'bot' && message.searchResults && message.searchResults.count > 0 && (
                      <div className="mt-1.5 w-full rounded-lg border border-accent bg-accent/30 p-2.5">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-foreground">
                            {message.searchResults.type === 'providers' ? '🔍 Prestataires trouvés' : '📋 Missions disponibles'}
                          </span>
                          <span className="text-xs font-bold bg-primary text-primary-foreground rounded-full px-2 py-0.5">
                            {message.searchResults.count}
                          </span>
                        </div>
                        {message.searchResults.categories.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {message.searchResults.categories.map((cat, idx) => (
                              <span key={idx} className="text-xs bg-muted text-muted-foreground rounded px-1.5 py-0.5">
                                {cat}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Navigation action buttons */}
                    {message.type === 'bot' && message.navigationActions && message.navigationActions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1 w-full">
                        {message.navigationActions.map((action, idx) => (
                          <Button
                            key={idx}
                            variant="outline"
                            size="sm"
                            className="text-xs h-8 gap-1.5 border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                            onClick={() => handleNavigate(action.path)}
                          >
                            <ExternalLink className="h-3 w-3" />
                            {action.label}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-lg p-3">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <div className={`border-t ${isMobile ? 'p-2' : 'p-4'}`}>
            <div className={`flex ${isMobile ? 'gap-1.5' : 'gap-2'}`}>
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Posez votre question..."
                disabled={isLoading}
                className="flex-1"
              />
              <Button
                onClick={handleSendClick}
                disabled={!input.trim() || isLoading}
                size="icon"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};
